import React, { useState, useEffect } from 'react';
import { useDevices } from '../store/devices';
import { api } from '../store/auth';
import toast from 'react-hot-toast';

export default function BulkDeploy() {
  const { devices, fetch } = useDevices();
  const [selected, setSelected] = useState(new Set());
  const [deployStatus, setDeployStatus] = useState(null);
  const [installUrl, setInstallUrl] = useState('');
  const [wingetId, setWingetId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deployInfo, setDeployInfo] = useState(null);
  const [bulkCmd, setBulkCmd] = useState('');
  const [bulkShellType, setBulkShellType] = useState('cmd');

  useEffect(() => {
    fetch();
    api.get('/deploy/status').then(r => setDeployInfo(r.data)).catch(() => {});
  }, []);

  const online = devices.filter(d => d.is_online);
  const toggleAll = () => {
    if (selected.size === online.length) setSelected(new Set());
    else setSelected(new Set(online.map(d => d.device_id)));
  };
  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const bulkInstall = async () => {
    if (!selected.size) { toast.error('Select at least one device'); return; }
    if (!installUrl && !wingetId) { toast.error('Enter a download URL or Winget ID'); return; }
    const { bulkCommand } = useDevices.getState();
    const results = await bulkCommand('install', {
      device_ids: [...selected],
      url: installUrl,
      winget_id: wingetId || undefined,
    });
    setDeployStatus(results);
    toast.success('Install commands sent');
  };

  const bulkPower = async (action) => {
    if (!selected.size) { toast.error('Select at least one device'); return; }
    if (!window.confirm(`Send "${action}" to ${selected.size} devices?`)) return;
    const { bulkCommand } = useDevices.getState();
    const results = await bulkCommand('power', { device_ids: [...selected], action });
    setDeployStatus(results);
    toast.success(`${action} sent to ${selected.size} devices`);
  };

  const uploadInstaller = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await api.post('/deploy/upload/installer', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Installer uploaded (${r.data.size_mb} MB)`);
      setDeployInfo(await api.get('/deploy/status').then(r => r.data));
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <h2 style={{ fontWeight:700, marginBottom:24 }}>Bulk Deploy & Management</h2>

      {/* Installer hosting */}
      <Section title="Agent Installer Hosting">
        <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
          <div>
            <label style={labelStyle}>Upload ClassRMM_Setup.exe to server</label>
            <input type="file" accept=".exe" onChange={uploadInstaller}
              style={{ color:'var(--text)', fontSize:13 }} />
            {uploading && <span style={{ color:'var(--muted)', fontSize:12 }}> Uploading…</span>}
          </div>
          {deployInfo?.installer?.exists && (
            <div style={{ background:'var(--bg)', border:'1px solid var(--border)',
              borderRadius:6, padding:12, fontSize:12 }}>
              <div style={{ color:'var(--green)', marginBottom:4 }}>✓ Installer hosted ({deployInfo.installer.size_mb} MB)</div>
              <div style={{ color:'var(--muted)' }}>URL: {deployInfo.installer.url}</div>
              <a href="/deploy/link" target="_blank"
                style={{ color:'var(--accent)', display:'block', marginTop:6 }}>
                📋 View install commands & one-click link →
              </a>
            </div>
          )}
        </div>
      </Section>

      {/* Device selector */}
      <Section title={`Select Target Devices (${selected.size} selected)`}>
        <div style={{ marginBottom:10 }}>
          <button onClick={toggleAll} style={btnStyle}>
            {selected.size === online.length ? 'Deselect All' : `Select All Online (${online.length})`}
          </button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:8 }}>
          {devices.map(d => (
            <label key={d.device_id} style={{
              display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
              background: selected.has(d.device_id) ? 'var(--accent)15' : 'var(--bg)',
              border:`1px solid ${selected.has(d.device_id) ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius:6, cursor: d.is_online ? 'pointer' : 'not-allowed',
              opacity: d.is_online ? 1 : 0.4,
            }}>
              <input type="checkbox" checked={selected.has(d.device_id)}
                disabled={!d.is_online}
                onChange={() => d.is_online && toggle(d.device_id)} />
              <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
                background: d.is_online ? 'var(--green)' : 'var(--muted)' }} />
              <span style={{ fontSize:12, overflow:'hidden', textOverflow:'ellipsis',
                whiteSpace:'nowrap' }}>{d.label || d.hostname}</span>
            </label>
          ))}
        </div>
      </Section>

      {/* App install */}
      <Section title="Push Application Install">
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12 }}>
          <div style={{ flex:1, minWidth:200 }}>
            <label style={labelStyle}>Download URL (.exe or .msi)</label>
            <input value={installUrl} onChange={e => setInstallUrl(e.target.value)}
              placeholder="https://example.com/setup.exe" style={{ ...inputStyle, width:'100%' }} />
          </div>
          <div style={{ flex:1, minWidth:200 }}>
            <label style={labelStyle}>OR Winget Package ID</label>
            <input value={wingetId} onChange={e => setWingetId(e.target.value)}
              placeholder="Google.Chrome" style={{ ...inputStyle, width:'100%' }} />
          </div>
        </div>
        <button onClick={bulkInstall} style={{ ...btnStyle, color:'var(--accent)',
          borderColor:'var(--accent)' }}>
          ⬇ Install on {selected.size} device{selected.size !== 1 ? 's' : ''}
        </button>
      </Section>

      {/* Bulk Shell Command */}
      <Section title="Bulk Shell Command">
        <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
            {['cmd', 'powershell'].map(t => (
              <button key={t} onClick={() => setBulkShellType(t)} style={{
                padding:'6px 12px', border:'none', fontSize:11, fontWeight: bulkShellType === t ? 600 : 400,
                background: bulkShellType === t ? 'var(--accent)' : 'transparent',
                color: bulkShellType === t ? '#fff' : 'var(--muted)', cursor:'pointer',
              }}>{t.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <input value={bulkCmd} onChange={e => setBulkCmd(e.target.value)}
            placeholder={bulkShellType === 'powershell' ? 'Get-Process | Select -First 10' : 'ipconfig /all'}
            style={{ ...inputStyle, flex:1, fontFamily:'monospace' }} />
          <button onClick={async () => {
            if (!selected.size) { toast.error('Select at least one device'); return; }
            if (!bulkCmd.trim()) { toast.error('Enter a command'); return; }
            const { bulkCommand } = useDevices.getState();
            const results = await bulkCommand('shell', {
              device_ids: [...selected], cmd: bulkCmd, shell_type: bulkShellType,
            });
            setDeployStatus(results);
            toast.success(`Command sent to ${selected.size} device(s)`);
          }} style={{ ...btnStyle, color:'var(--accent)', borderColor:'var(--accent)' }}>
            Run on {selected.size} device{selected.size !== 1 ? 's' : ''}
          </button>
        </div>
        <div style={{ fontSize:11, color:'var(--muted)' }}>
          Results will appear in each device's terminal view.
        </div>
      </Section>

      {/* Power control */}
      <Section title="Bulk Power Control">
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {[
            ['🔒 Lock',     'lock',     false],
            ['💤 Sleep',    'sleep',    false],
            ['🔄 Restart',  'restart',  true],
            ['⏻ Shutdown',  'shutdown', true],
          ].map(([label, action, danger]) => (
            <button key={action} onClick={() => bulkPower(action)} style={{
              ...btnStyle,
              color: danger ? 'var(--red)' : 'var(--muted)',
              borderColor: danger ? 'var(--red)30' : 'var(--border)',
            }}>{label} ({selected.size})</button>
          ))}
        </div>
      </Section>

      {/* Results */}
      {deployStatus && (
        <Section title="Last Command Results">
          <div style={{ fontFamily:'monospace', fontSize:12 }}>
            {Object.entries(deployStatus).map(([id, status]) => {
              const d = devices.find(x => x.device_id === id);
              return (
                <div key={id} style={{ display:'flex', gap:12, padding:'4px 0',
                  borderBottom:'1px solid var(--border)', alignItems:'center' }}>
                  <span style={{ color:'var(--accent)', width:120 }}>
                    {d?.label || d?.hostname || id.slice(0,8)}
                  </span>
                  <span style={{ color: status === 'sent' ? 'var(--green)' : 'var(--red)' }}>
                    {status === 'sent' ? '✓ sent' : '✗ offline'}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)',
      borderRadius:8, padding:20, marginBottom:16 }}>
      <div style={{ fontWeight:600, marginBottom:14, fontSize:13, color:'var(--muted)',
        textTransform:'uppercase', letterSpacing:.5 }}>{title}</div>
      {children}
    </div>
  );
}

const labelStyle = { display:'block', fontSize:11, color:'var(--muted)', marginBottom:4 };
const btnStyle = {
  padding:'7px 14px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--muted)', cursor:'pointer', fontSize:13,
};
const inputStyle = {
  padding:'8px 10px', background:'var(--bg)', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--text)', fontSize:13,
};
